<script setup>
  import {ref, watch} from "vue";

  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentEditor from "@components/Editor/Editor.vue";
  import {useForm} from "@inertiajs/vue3";
  import {MedButton, MedInfoButton} from "@pharma4u/medicheck-components";

  defineOptions({name: "ComponentPatientNoteDialog"});

  const props = defineProps({
    patient: {
      type: Object,
      default: () => ({}),
    },
  });

  const refDialog = ref(null);

  const currentPatient = ref(props.patient);

  const form = useForm({
    note: currentPatient.value?.note,
  });

  watch(currentPatient, (patient) => {
    form.defaults({note: patient?.note}).reset();
  });

  function open(patient) {
    if (patient !== undefined) {
      currentPatient.value = patient;
    }
    form.resetAndClearErrors();
    refDialog.value.open();
  }

  /**
   * Closes the dialog without resetting the form, so that Inertia can adopt the submitted note as the new default
   * after a successful save.
   */
  function closeDialog() {
    refDialog.value?.close();
  }

  /**
   * Discards unsaved changes so that the last saved note is shown again on the next open.
   */
  function cancel() {
    form.resetAndClearErrors();
    closeDialog();
  }

  function save() {
    form.patch(route("patients.update", {patient: currentPatient.value.id}), {
      onSuccess: () => {
        closeDialog();
      },
    });
  }

  defineExpose({
    open,
    close: cancel,
  });
</script>

<template>
  <ComponentDialog ref="refDialog" dialog-width="medium" :test-id="getTestId('patient-note')">
    <template #title>
      Patientennotiz für
      <span :data-encrypted="currentPatient.firstname" />
      <span :data-encrypted="currentPatient.lastname" />
    </template>

    <template #content>
      <form :data-test="getTestId('patient-note-dialog-form')" @submit.prevent="save">
        <ComponentEditor
          v-model="form.note"
          editor-class="h-52 max-h-[75vh]"
          :encrypted="true"
          :test-id="getTestId('patient-note-dialog-form-note')"
        />
      </form>
    </template>

    <template #actions>
      <MedButton
        variant="primary"
        :disabled="form.processing"
        :data-test="getTestId('patient-note-dialog-save-button')"
        @click="save()"
      >
        Speichern
      </MedButton>

      <MedButton :data-test="getTestId('patient-note-dialog-close-button')" @click="cancel()">Abbrechen</MedButton>
    </template>

    <template #info-icon>
      <MedInfoButton
        size="lg"
        side="top"
        aria-label="Patientennotiz Info"
        disable-portal
        :data-test="getTestId('patient-note-dialog-info-button')"
      >
        <p class="text-sm">
          Zu Ihrem Patienten können Sie beliebige Notizen festhalten. Sie haben überall im MediCheck Zugriff auf die
          Notizen zu Ihrem Patienten.
        </p>
      </MedInfoButton>
    </template>
  </ComponentDialog>
</template>
